import React, { useState, useEffect } from 'react';
import { 
  Building, Plus, Edit, Trash2, Save, X, 
  Users, DollarSign, CheckCircle, AlertCircle
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const OrganizationTypesManagement = () => {
  const [orgTypes, setOrgTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    employee_range: '',
    revenue_range: '',
    qualitative_questions: 10,
    quantitative_questions: 5,
    assessment_fee: 1500,
    order: 1,
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchOrgTypes();
  }, []);

  const fetchOrgTypes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/organization-types`);
      if (response.ok) {
        const data = await response.json();
        setOrgTypes(data.organization_types || []);
      }
    } catch (error) {
      console.error('Error fetching organization types:', error);
      setError('Failed to load organization types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingType(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      employee_range: '',
      revenue_range: '',
      qualitative_questions: 10,
      quantitative_questions: 5,
      assessment_fee: 1500,
      order: orgTypes.length + 1,
      is_active: true
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleEdit = (orgType) => {
    setEditingType(orgType);
    setFormData({
      name: orgType.name || '',
      display_name: orgType.display_name || '',
      description: orgType.description || '',
      employee_range: orgType.employee_range || '',
      revenue_range: orgType.revenue_range || '',
      qualitative_questions: orgType.qualitative_questions || 10,
      quantitative_questions: orgType.quantitative_questions || 5,
      assessment_fee: orgType.assessment_fee || 1500,
      order: orgType.order || 1,
      is_active: orgType.is_active !== false
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!formData.name || !formData.display_name) {
      setError('Name and Display Name are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const url = editingType 
        ? `${BACKEND_URL}/api/admin/organization-types/${editingType.id}`
        : `${BACKEND_URL}/api/admin/organization-types`;
      
      const method = editingType ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          name: formData.name.toUpperCase()
        })
      });

      if (response.ok) {
        setSuccess(editingType ? 'Organization type updated!' : 'Organization type created!');
        fetchOrgTypes();
        setTimeout(() => {
          setShowModal(false);
          setSuccess('');
        }, 1500);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (orgType) => {
    if (!window.confirm(`Are you sure you want to delete "${orgType.display_name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/organization-types/${orgType.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchOrgTypes();
      } else {
        alert('Failed to delete organization type');
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    }
  };

  return (
    <SystemAdminLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-black text-gray-900 flex items-center gap-3">
                <Building className="w-8 h-8 text-indigo-600" />
                Organization Types Master
              </h1>
              <p className="text-gray-600 mt-1">
                Manage organization size categories, assessment fees, and question counts
              </p>
            </div>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Add Organization Type
            </button>
          </div>

          {/* Organization Types Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {orgTypes.map((orgType) => (
                <div
                  key={orgType.id}
                  className={`bg-white rounded-2xl border-2 ${
                    orgType.is_active ? 'border-gray-200' : 'border-red-200 bg-red-50'
                  } shadow-lg overflow-hidden hover:shadow-xl transition-all`}
                >
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-black">{orgType.display_name || orgType.name}</h3>
                      <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm font-bold">
                        #{orgType.order || '-'}
                      </span>
                    </div>
                    <p className="text-indigo-100 text-sm mt-1">{orgType.name}</p>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    {orgType.description && (
                      <p className="text-gray-600 text-sm">{orgType.description}</p>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <Users className="w-3 h-3" />
                          Employees
                        </div>
                        <p className="font-bold text-gray-900">{orgType.employee_range || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <DollarSign className="w-3 h-3" />
                          Revenue
                        </div>
                        <p className="font-bold text-gray-900">{orgType.revenue_range || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="bg-indigo-50 rounded-lg p-3">
                      <p className="text-xs text-indigo-600 font-semibold mb-2">Questions Required</p>
                      <div className="flex justify-between">
                        <span className="text-gray-700">
                          Qualitative: <strong>{orgType.qualitative_questions}</strong>
                        </span>
                        <span className="text-gray-700">
                          Quantitative: <strong>{orgType.quantitative_questions}</strong>
                        </span>
                      </div>
                      <p className="text-center mt-2 font-bold text-indigo-700">
                        Total: {(orgType.qualitative_questions || 0) + (orgType.quantitative_questions || 0)} questions
                      </p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-green-600 font-semibold">Assessment Fee</p>
                      <p className="text-2xl font-black text-green-700">
                        ${orgType.assessment_fee?.toLocaleString() || '0'}
                      </p>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleEdit(orgType)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-all"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(orgType)}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-black">
                    {editingType ? 'Edit Organization Type' : 'Add Organization Type'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                  </div>
                )}
                {success && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                    {success}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Internal Name * (uppercase)
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                      placeholder="e.g., MICRO"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Display Name *
                    </label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      placeholder="e.g., Micro Business"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this organization type..."
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Employee Range
                    </label>
                    <input
                      type="text"
                      value={formData.employee_range}
                      onChange={(e) => setFormData({ ...formData, employee_range: e.target.value })}
                      placeholder="e.g., 1-10"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Revenue Range
                    </label>
                    <input
                      type="text"
                      value={formData.revenue_range}
                      onChange={(e) => setFormData({ ...formData, revenue_range: e.target.value })}
                      placeholder="e.g., <$1M"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Qualitative Questions
                    </label>
                    <input
                      type="number"
                      value={formData.qualitative_questions}
                      onChange={(e) => setFormData({ ...formData, qualitative_questions: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Quantitative Questions
                    </label>
                    <input
                      type="number"
                      value={formData.quantitative_questions}
                      onChange={(e) => setFormData({ ...formData, quantitative_questions: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Assessment Fee ($)
                    </label>
                    <input
                      type="number"
                      value={formData.assessment_fee}
                      onChange={(e) => setFormData({ ...formData, assessment_fee: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 1 })}
                      min="1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.is_active ? 'active' : 'inactive'}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-indigo-700">
                    <strong>Total Questions:</strong> {(formData.qualitative_questions || 0) + (formData.quantitative_questions || 0)}
                    {' | '}
                    <strong>Assessment Fee:</strong> ${formData.assessment_fee?.toLocaleString() || 0}
                  </p>
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-b-2xl border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {editingType ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SystemAdminLayout>
  );
};

export default OrganizationTypesManagement;
